// Utilities for code-based FS node identification and lookup
// This avoids brittle label/alias/language conditions.

export function normalizeCode(input?: string | null): string {
  return String(input || '').trim().toUpperCase();
}

export function isCode(itemOrCode: { code?: string | null } | string | null | undefined, ...codes: string[]): boolean {
  const val = typeof itemOrCode === 'string' || itemOrCode == null ? (itemOrCode as any) : itemOrCode.code;
  const c = normalizeCode(val);
  const set = new Set(codes.map(normalizeCode));
  return set.has(c);
}

export function isCodeIn(itemOrCode: { code?: string | null } | string | null | undefined, codes: string[] = []): boolean {
  return isCode(itemOrCode as any, ...codes);
}

export function findNodeByCodes<T extends { code?: string | null; children?: T[] }>(nodes: T[] | undefined | null, codes: string[]): T | null {
  if (!nodes || !Array.isArray(nodes)) return null;
  const targets = new Set((codes || []).map(normalizeCode));
  const stack: T[] = [...nodes];
  while (stack.length) {
    const n = stack.shift() as T;
    if (targets.has(normalizeCode(n?.code))) return n;
    if (Array.isArray(n?.children) && n!.children!.length) stack.unshift(...(n!.children as T[]));
  }
  return null;
}

// Centralized codes derived from the provided dictionary
export const FS_CODE = {
  BS: {
    CURRENT_ASSETS: ['BS-CA'],
    CURRENT_LIABILITIES: ['BS-CL'],
    NON_CURRENT_ASSETS: ['BS-NCA'],
    NON_CURRENT_LIABILITIES: ['BS-NCL'],
    NET_CURRENT: ['BS-NCA-RES'],
    NET_ASSETS: ['BS-NL-RES'],
    CAPITAL_AND_RESERVES: ['BS-EQ'],
    ISSUED_CAPITAL: ['BS-EQ-IC'],
    RETAINED_PROFITS_OR_LOSSES: ['BS-EQ-AL'],
    PROFIT_LOSS_FOR_YEAR_PERIOD: ['BS-EQ-LFP'],
    DIVIDEND: ['BS-EQ-DIV'],
    DUE_TO_DIRECTOR: ['BS-CL-DTD'],
    DUE_FROM_DIRECTOR: ['BS-CA-DIR'],
    // Vehicle Loan (Obligation under finance lease)
    VEHICLE_LOAN_CURRENT: ['BS-CL-OFL'],
    VEHICLE_LOAN_NON_CURRENT: ['BS-NCL-OFL'],
    // Bank Loan
    BANK_LOAN_CURRENT: ['BS-CL-BLS'],
    BANK_LOAN_NON_CURRENT: ['BS-NCL-BLS'],
    // Inventories
    INVENTORIES: ['BS-CA-INV'],
  },
  PL: {
    TURNOVER: ['PL-TURN'],
    COST_OF_TURNOVER: ['PL-COT'],
    GROSS: ['PL-GP'],
    OTHER_INCOME: ['PL-OI'],
    GROSS_AND_OTHER: ['PL-GPOI'],
    ADMIN_EXPENSES: ['PL-AE'],
    FINANCE_COSTS: ['PL-FC'],
    BEFORE_TAX: ['PL-PBT'],
    TAXATION: ['PL-TAX'],
    AFTER_TAX: ['PL-PAT'],
    // Cost of Turnover sub-items
    OPENING_INVENTORIES: ['PL-COT-OI'],
    PURCHASES: ['PL-COT-PUR'],
    CLOSING_INVENTORIES: ['PL-COT-CI'],
    COST_OF_SALES: ['PL-COT-COS'],
  },
};

export const BS_HEADER_CODES = [
  ...FS_CODE.BS.CURRENT_ASSETS,
  ...FS_CODE.BS.CURRENT_LIABILITIES,
  ...FS_CODE.BS.NON_CURRENT_ASSETS,
  ...FS_CODE.BS.NON_CURRENT_LIABILITIES,
];
